import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";

type DeploymentStatusTagProps =
  | {
      variant: "page";
      file: FileWithLatestFileVersion;
    }
  | {
      variant: "funnel";
      pages: FileWithLatestFileVersion[];
    };

export const DeploymentStatusTag = (props: DeploymentStatusTagProps) => {
  const projectId = useCurrentProjectId();

  const { data: pendingDeployments } =
    trpc.v2ShopDeployment.findDeployments.useQuery(
      projectId
        ? {
            projectId,
            deployStatus: "PENDING",
          }
        : skipToken,
    );

  if (!projectId || !pendingDeployments) {
    return null;
  }

  let isDraft = false;
  let isPendingDeployment = false;
  let hasUnpublishedChanges = false;
  let isPublishedWithNoChanges = false;

  if (props.variant === "page") {
    const { file } = props;

    isPendingDeployment = pendingDeployments.some((deployment) =>
      deployment.filesPublished.some(
        (publishedFile) => publishedFile.id === file.id,
      ),
    );

    isDraft = !isPendingDeployment && file.lastPublishedFileVersionId === null;

    hasUnpublishedChanges =
      !isPendingDeployment &&
      file.latestFileVersion.id !== file.lastPublishedFileVersionId;

    isPublishedWithNoChanges =
      !isPendingDeployment &&
      file.lastPublishedFileVersionId === file.latestFileVersion.id;
  } else if (props.variant === "funnel") {
    const { pages } = props;

    isPendingDeployment = pendingDeployments.some((deployment) =>
      deployment.filesPublished.some((publishedFile) =>
        pages.some((page) => page.id === publishedFile.id),
      ),
    );

    isDraft =
      !isPendingDeployment &&
      pages.some((page) => page.lastPublishedFileVersionId === null);

    hasUnpublishedChanges =
      !isPendingDeployment &&
      pages.some(
        (page) => page.latestFileVersion.id !== page.lastPublishedFileVersionId,
      );

    isPublishedWithNoChanges =
      !isPendingDeployment &&
      pages.every(
        (page) => page.lastPublishedFileVersionId === page.latestFileVersion.id,
      );
  }

  if (isDraft) {
    return (
      <Tag size="base" variant="neutral">
        Draft
      </Tag>
    );
  } else if (isPendingDeployment) {
    return (
      <Tag size="base" variant="info">
        Publishing...
      </Tag>
    );
  } else if (hasUnpublishedChanges) {
    return (
      <Tag size="base" variant="info">
        Edited
      </Tag>
    );
  } else if (isPublishedWithNoChanges) {
    return (
      <Tag size="base" variant="success">
        Published
      </Tag>
    );
  }
};
